import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import _ from 'lodash';
import VirtualList from 'rc-virtual-list';
// Slices
import { selectObjectTypesList } from 'slices/objectTypes.slice';
// Utils
import { matchString } from 'utils/stringUtils';
import type { TMemberTypesItem } from '@timeedit/types/lib/types';
// Types
// Components
import { Button, Icon, TEDrawerSection } from '@timeedit/ui-components';
import { Card, Select, Typography } from '@timeedit/ui-components/antd';
import type { IProps } from './sections.interface';
// Styles
import './ObjectTypeHierarchy.scss';

interface ITypeLevel {
  types: number[];
}

const ObjectTypeHierarchy = ({ membership, onChange }: IProps) => {
  const objectTypes = useSelector(selectObjectTypesList);
  const { memberTypes = [] } = membership;
  const [typeLevels, setTypeLevels] = useState<ITypeLevel[]>([]);

  useEffect(() => {
    const typesGroupedByLevel = _.groupBy(memberTypes, 'memberLevel');
    const levels: ITypeLevel[] = [];

    for (const types of Object.values(typesGroupedByLevel)) {
      levels.push({
        types: types.map((type) => type.type as number),
      });
    }

    setTypeLevels(levels);
  }, [memberTypes]);

  const handleChange = (typeLevels: ITypeLevel[]) => {
    const memTypes: TMemberTypesItem[] = typeLevels.flatMap(
      (typeLevel, index) =>
        typeLevel.types.map((type) => ({ type, memberLevel: index }))
    );
    onChange('memberTypes', memTypes);
  };

  const onAddLevel = () => {
    setTypeLevels((prev) => [
      ...prev,
      {
        types: [],
      },
    ]);
  };

  const onDeleteLevel = (level: number) => {
    const newTypeLevels = typeLevels.filter((_, index) => index !== level);
    if (typeLevels[level].types.length === 0) {
      setTypeLevels(newTypeLevels);
    } else {
      handleChange(newTypeLevels);
    }
  };

  const onTypeChange = (types: number[], level: number) => {
    const newTypeLevels = [...typeLevels];
    newTypeLevels[level].types = types;
    handleChange(newTypeLevels);
  };

  return (
    <TEDrawerSection label="Object type hierarchy">
      <Card>
        <div className="membership-type-hierarchy">
          <VirtualList data={typeLevels} itemKey={'level'}>
            {(item: ITypeLevel, index: number) => (
              <div key={index} className="type-row">
                <Typography.Text strong>{index + 1}.</Typography.Text>
                <Select
                  data-testid={`object-type-${index}`}
                  size="small"
                  mode="multiple"
                  value={item.types}
                  className="type-select"
                  onChange={(types: number[]) => onTypeChange(types, index)}
                  options={objectTypes.map((type) => ({
                    value: type.id,
                    label: type.name,
                  }))}
                  filterOption={(input, option) =>
                    matchString(option?.label, input)
                  }
                />

                <Button
                  icon={<Icon id="DeleteOutlined" alt="Delete" />}
                  type="text"
                  onClick={() => onDeleteLevel(index)}
                ></Button>
              </div>
            )}
          </VirtualList>
          <div className="add-btn" data-testid="add-object-type">
            <Typography.Link onClick={onAddLevel}>Add level</Typography.Link>
          </div>
        </div>
      </Card>
    </TEDrawerSection>
  );
};

export default ObjectTypeHierarchy;
